import React, { createContext, useContext, useCallback } from 'react';
import { SnackbarProvider as NotistackProvider, useSnackbar } from 'notistack';
import { Alert, Slide } from '@mui/material';
import PropTypes from 'prop-types';

const TransitionTypes = {
  Slide: Slide,
};

const NotificationContext = createContext(null);

const NotificationImplementation = ({ children }) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const showNotification = useCallback((config) => {
    const validConfig = typeof config === 'object' ? config : { message: String(config) };

    const originalSeverity = validConfig.severity;

    const normalizeSeverity = (sev) => {
      if (!sev) return 'info';
      const severityString = String(sev).toLowerCase();
      const validSeverities = ['success', 'error', 'warning', 'info'];
      return validSeverities.includes(severityString) ? severityString : 'info';
    };

    const {
      message = 'Notificación',
      severity = 'info',
      position,
      autoHideDuration = 3000,
      transition = TransitionTypes.Slide
    } = validConfig;

    const normalizedSeverity = normalizeSeverity(severity);

    const validPosition = position &&
      typeof position === 'object' &&
      position.vertical &&
      position.horizontal ? position :
      { vertical: 'top', horizontal: 'right' };

    enqueueSnackbar(message, {
      variant: normalizedSeverity,
      anchorOrigin: validPosition,
      autoHideDuration,
      TransitionComponent: transition,
      onClose: (event, reason, key) => {
        if (reason === 'timeout') {
          closeSnackbar(key);
        }
      },
      content: (key) => {
        const shouldUseWhiteBackground = !originalSeverity ||
          originalSeverity === 'info' ||
          String(originalSeverity).toLowerCase() === 'info';

        return (
          <Alert
            elevation={6}
            variant="filled"
            onClose={() => closeSnackbar(key)}
            severity={normalizedSeverity}
            sx={{
              width: '100%',
              zIndex: 1000,
              position: 'relative',
              ...(shouldUseWhiteBackground && {
                backgroundColor: '#ffffff !important',
                color: '#333333 !important',
                border: '1px solid #e0e0e0',
                '& .MuiAlert-icon': {
                  color: '#666666 !important'
                },
                '& .MuiAlert-action': {
                  color: '#666666 !important'
                }
              }),
              '& .MuiAlert-message': {
                display: 'flex',
                alignItems: 'center'
              },
              boxShadow: '0 4px 20px 0 rgba(0,0,0,0.12), 0 7px 8px -5px rgba(0,0,0,0.2)'
            }}
          >
            {message}
          </Alert>
        );
      }
    });
  }, [enqueueSnackbar, closeSnackbar]);

  return (
    <NotificationContext.Provider value={{ showNotification }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const NotificationProvider = ({ children }) => {
  return (
    <NotistackProvider
      maxSnack={3}
      preventDuplicate
      dense
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right'
      }}
      TransitionComponent={Slide}
      style={{
        marginTop: '10px',
        zIndex: 9999
      }}
      classes={{
        containerRoot: 'notification-container-root',
        containerAnchorOriginTopRight: 'notification-top-right',
        containerAnchorOriginTopLeft: 'notification-top-left',
        containerAnchorOriginBottomRight: 'notification-bottom-right',
        containerAnchorOriginBottomLeft: 'notification-bottom-left'
      }}
    >
      <NotificationImplementation>
        {children}
      </NotificationImplementation>
    </NotistackProvider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification debe usarse dentro de un NotificationProvider');
  }
  return context;
};

NotificationProvider.propTypes = {
  children: PropTypes.node.isRequired
};

NotificationImplementation.propTypes = {
  children: PropTypes.node.isRequired
};

export const NotificationSeverity = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info'
};

export const NotificationPositions = {
  TOP_RIGHT: { vertical: 'top', horizontal: 'right' },
  TOP_LEFT: { vertical: 'top', horizontal: 'left' },
  BOTTOM_RIGHT: { vertical: 'bottom', horizontal: 'right' },
  BOTTOM_LEFT: { vertical: 'bottom', horizontal: 'left' }
};

export const NOTIFICATION_GLOBAL_STYLES = `
  .notification-container-root {
    z-index: 9999 !important;
    position: fixed !important;
  }
  
  .notification-top-right {
    top: 16px !important;
    right: 16px !important;
    z-index: 9999 !important;
  }
  
  .notification-top-left {
    top: 16px !important;
    left: 16px !important;
    z-index: 9999 !important;
  }
  
  .notification-bottom-right {
    bottom: 16px !important;
    right: 16px !important;
    z-index: 9999 !important;
  }
  
  .notification-bottom-left {
    bottom: 16px !important;
    left: 16px !important;
    z-index: 9999 !important;
  }
  
  .SnackbarContainer-root {
    z-index: 9999 !important;
  }
  
  .SnackbarItem-root {
    z-index: 9999 !important;
  }
`;